import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router"
import { useAuthStore } from "@/shared/hooks/use-auth-store"
import { useToast } from "@/client/components/common/toast"
import { getBrowserSupabaseClient } from "@/lib/supabase/client"
import styles from "./verify-page.module.css"

interface VerifyApplication {
  id: string
  user_id: string
  verify_type: string
  evidence_url: string | null
  status: "pending" | "approved" | "rejected"
  reviewer_id: string | null
  review_note: string | null
  created_at: string
  reviewed_at: string | null
}

interface VerifyTypeOption {
  key: string
  name: string
  icon: string
  desc: string
  detail: string
}

const VERIFY_TYPES: VerifyTypeOption[] = [
  {
    key: "enterprise",
    name: "企业认证",
    icon: "🏢",
    desc: "企业用户",
    detail: "适用于企业用户，需提供企业营业执照",
  },
  {
    key: "engineer",
    name: "个体认证",
    icon: "👨‍💻",
    desc: "个人开发者",
    detail: "适用于个人开发者，需提供作品集或GitHub主页",
  },
  {
    key: "student",
    name: "学生认证",
    icon: "🎓",
    desc: "在校学生",
    detail: "适用于在校学生，需提供学生证或学信网截图",
  },
]

const TYPE_NAME_MAP: Record<string, string> = {
  enterprise: "企业认证",
  engineer: "个体认证",
  student: "学生认证",
}

function isValidUrl(text: string): boolean {
  try {
    const url = new URL(text)
    return url.protocol === "http:" || url.protocol === "https:"
  } catch {
    return false
  }
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

export default function VerifyPage() {
  const { user } = useAuthStore()
  const toast = useToast()
  const navigate = useNavigate()

  const [selectedType, setSelectedType] = useState<string | null>(null)
  const [evidenceUrl, setEvidenceUrl] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [applications, setApplications] = useState<VerifyApplication[]>([])
  const [loading, setLoading] = useState(true)

  const fetchApplications = useCallback(async () => {
    const supabase = getBrowserSupabaseClient()
    if (!supabase || !user) return

    const { data, error } = await supabase
      .from("verify_applications")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })

    if (error) {
      toast.error("加载认证记录失败")
      return
    }

    setApplications((data as VerifyApplication[]) ?? [])
    setLoading(false)
  }, [user, toast])

  useEffect(() => {
    fetchApplications()
  }, [fetchApplications])

  const selectedOption = VERIFY_TYPES.find((t) => t.key === selectedType)

  const pendingTypes = new Set(
    applications.filter((a) => a.status === "pending").map((a) => a.verify_type),
  )

  async function handleSubmit() {
    if (!selectedType) {
      toast.warning("请先选择认证类型")
      return
    }

    const trimmed = evidenceUrl.trim()
    if (!trimmed) {
      toast.warning("请输入证明材料链接")
      return
    }

    if (!isValidUrl(trimmed)) {
      toast.warning("请输入有效的URL地址")
      return
    }

    if (pendingTypes.has(selectedType)) {
      toast.warning("该类型已有审核中的申请，请勿重复提交")
      return
    }

    const supabase = getBrowserSupabaseClient()
    if (!supabase || !user) return

    setSubmitting(true)

    const { error } = await supabase
      .from("verify_applications")
      .insert({
        user_id: user.id,
        verify_type: selectedType,
        evidence_url: trimmed,
      })
      .select()
      .single()

    setSubmitting(false)

    if (error) {
      toast.error("提交失败，请稍后重试")
      return
    }

    toast.success("认证申请已提交")
    setEvidenceUrl("")
    setSelectedType(null)
    fetchApplications()
  }

  function handleBack() {
    navigate(-1)
  }

  function renderStatusTag(status: VerifyApplication["status"]) {
    switch (status) {
      case "pending":
        return <span className={`${styles.statusTag} ${styles.statusPending}`}>审核中</span>
      case "approved":
        return <span className={`${styles.statusTag} ${styles.statusApproved}`}>已通过</span>
      case "rejected":
        return <span className={`${styles.statusTag} ${styles.statusRejected}`}>已拒绝</span>
    }
  }

  return (
    <div className={styles.page}>
      {/* Sticky header */}
      <div className={styles.header}>
        <button className={styles.backBtn} onClick={handleBack} aria-label="返回">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <span className={styles.headerTitle}>认证中心</span>
      </div>

      <div className={styles.content}>
        {/* 认证类型选择 */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>认证类型</div>
          <div className={styles.typeCards}>
            {VERIFY_TYPES.map((t) => {
              const isActive = selectedType === t.key
              const isPending = pendingTypes.has(t.key)
              return (
                <button
                  key={t.key}
                  className={`${styles.typeCard} ${isActive ? styles.activeTypeCard : ""}`}
                  onClick={() => setSelectedType(t.key)}
                  disabled={isPending}
                  style={isPending ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                >
                  <div className={styles.typeIcon}>{t.icon}</div>
                  <div className={styles.typeName}>{t.name}</div>
                  <div className={styles.typeDesc}>{isPending ? "审核中" : t.desc}</div>
                </button>
              )
            })}
          </div>
        </div>

        {/* 认证说明 */}
        {selectedOption && (
          <div className={styles.section}>
            <div className={styles.sectionTitle}>认证说明</div>
            <div className={styles.descCard}>
              <span className={styles.descText}>{selectedOption.detail}</span>
            </div>
          </div>
        )}

        {/* 证明材料输入 */}
        {selectedType && (
          <>
            <div className={styles.formGroup}>
              <div className={styles.label}>证明材料</div>
              <input
                className={styles.input}
                type="url"
                placeholder="请输入证明材料链接（如营业执照、作品集URL等）"
                value={evidenceUrl}
                onChange={(e) => setEvidenceUrl(e.target.value)}
              />
            </div>
            <button
              className={styles.submitBtn}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? "提交中..." : "提交申请"}
            </button>
          </>
        )}

        {/* 我的认证记录 */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>我的认证记录</div>

          {loading ? (
            <div className={styles.loading}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--t3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83">
                  <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite" />
                </path>
              </svg>
            </div>
          ) : applications.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyText}>暂无认证记录</div>
            </div>
          ) : (
            <div className={styles.historyList}>
              {applications.map((app) => (
                <div key={app.id} className={styles.historyItem}>
                  <div className={styles.historyInfo}>
                    <span className={styles.historyType}>
                      {TYPE_NAME_MAP[app.verify_type] ?? app.verify_type}
                    </span>
                    <span className={styles.historyDate}>{formatDate(app.created_at)}</span>
                    {app.status === "rejected" && app.review_note && (
                      <span className={styles.historyNote}>{app.review_note}</span>
                    )}
                  </div>
                  {renderStatusTag(app.status)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
